import { useCallback, useEffect, useRef } from "react";
import {
  FilterConfig,
  applyLiveNoise,
  buildOutputFilterChain,
} from "../../../utils/telephonyFilters";

// Audio configuration
export const AUDIO_CONFIG = {
  // Audio Processing
  sampleRate: 48000,
  playbackRate: 1,

  // Fade Effects
  fadeDuration: 0.002,
  fadeInSteps: {
    step1: 0.9,
    step1Time: 0.002,
    step2: 1.0,
    step2Time: 0.005,
  },

  // Queue Processing
  queueDelay: 5,

  // Smoothing
  smoothingWindowSize: 3,
  enableSmoothing: true,
  enableResampling: true,

  // Streaming Audio Config
  streamingSampleRate: 16000, // TTS audio sample rate (16kHz)
  streamingBufferSize: 4096, // Samples to buffer before playing
  streamingMinBufferMs: 100, // Minimum buffer time before starting playback
};

interface AudioManagerProps {
  onTTSAudioComplete?: () => void;
  onTTSAudioStart?: () => void; // Called when audio actually starts playing
  onAllAudioComplete?: () => void; // Called when all audio in queue has finished playing
  onStreamingAudioStart?: () => void; // Called when streaming audio starts
  onStreamingAudioEnd?: () => void; // Called when streaming audio ends
  /** Optional telephony filter chain (Settings → Phone simulation). */
  filterConfig?: FilterConfig | null;
}

export const useAudioManager = ({
  onTTSAudioComplete,
  onTTSAudioStart,
  onAllAudioComplete,
  onStreamingAudioStart,
  onStreamingAudioEnd,
  filterConfig = null,
}: AudioManagerProps) => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  // Telephony filter chain — sits between gainNode and destination when
  // a Phone-simulation filter is configured. The gain node connects to
  // `filterEntryRef`; the chain internally connects to destination.
  const filterEntryRef = useRef<AudioNode | null>(null);
  const filterNoiseSrcRef = useRef<AudioBufferSourceNode | null>(null);
  const filterNoiseGainRef = useRef<GainNode | null>(null);
  const filterConfigRef = useRef<FilterConfig | null>(filterConfig);
  useEffect(() => {
    filterConfigRef.current = filterConfig;
    const ctx = audioContextRef.current;
    if (ctx)
      applyLiveNoise(ctx, filterNoiseGainRef.current, filterConfig, "output");
  }, [filterConfig]);
  const audioQueueRef = useRef<AudioBuffer[]>([]);
  const isPlayingRef = useRef<boolean>(false);
  const currentSourceRef = useRef<AudioBufferSourceNode | null>(null);

  // Playback stream for echo cancellation
  const playbackStreamRef = useRef<MediaStream | null>(null);
  const playbackDestinationRef = useRef<MediaStreamAudioDestinationNode | null>(
    null
  );

  // Track current playing text (for WebSocket audio tracking)
  const currentPlayingTextRef = useRef<string>("");

  // Track if server has finished sending all audio for current response
  const responseDoneRef = useRef<boolean>(false);
  // Track if audio was interrupted by barge-in (user spoke) — prevents stale playback.completed
  const bargedInRef = useRef<boolean>(false);

  // Streaming audio state
  const isStreamingRef = useRef<boolean>(false);
  const streamingBufferRef = useRef<Int16Array[]>([]);
  const streamingSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const streamingStartedRef = useRef<boolean>(false);
  const streamingScheduledTimeRef = useRef<number>(0);
  const streamingGainNodeRef = useRef<GainNode | null>(null);
  const streamingChunkCountRef = useRef<number>(0);

  // ---------- SETUP AUDIO CONTEXT ----------
  const setupAudioContext = useCallback(() => {
    if (
      !audioContextRef.current ||
      audioContextRef.current.state === "closed"
    ) {
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close();
      }

      audioContextRef.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)();

      gainNodeRef.current = audioContextRef.current.createGain();
      gainNodeRef.current.gain.value = 1.0;

      // Splice the telephony filter chain (Phone simulation) between
      // the gain node and the speaker destination. When `filterConfig`
      // is null/off the chain is a passthrough → no audible effect.
      const cfg = filterConfigRef.current;
      const outputChain = buildOutputFilterChain(
        audioContextRef.current,
        cfg,
        audioContextRef.current.destination
      );
      filterEntryRef.current = outputChain.entry;
      filterNoiseSrcRef.current = outputChain.noiseSource;
      filterNoiseGainRef.current = outputChain.noiseGain;
      gainNodeRef.current.connect(outputChain.entry);

      // Create playback destination for echo cancellation
      playbackDestinationRef.current =
        audioContextRef.current.createMediaStreamDestination();
      playbackStreamRef.current = playbackDestinationRef.current.stream;

      // Connect gain node to playback destination too (for echo
      // cancellation reference). The AEC reference path bypasses the
      // telephony filter chain so the browser's echo canceller still
      // sees what was rendered to the speaker, not the pre-filter
      // signal — keeps AEC accurate.
      gainNodeRef.current.connect(playbackDestinationRef.current);
    }

    audioQueueRef.current = [];
    isPlayingRef.current = false;

    if (currentSourceRef.current) {
      try {
        currentSourceRef.current.stop();
      } catch (error) {
        // Source might already be stopped
      }
      currentSourceRef.current = null;
    }

    if (audioContextRef.current.state === "suspended") {
      audioContextRef.current.resume();
    }
  }, []);

  // ---------- RESET AUDIO COMPLETELY ----------
  const resetAudioCompletely = useCallback(() => {
    const wasPlaying = isPlayingRef.current;

    // Clear pending sources first
    for (const source of pendingSourcesRef.current) {
      try {
        source.stop();
      } catch (error) {
        // Source might already be stopped
      }
    }
    pendingSourcesRef.current = [];
    scheduledEndTimeRef.current = 0;
    isPlayingRef.current = false;
    responseDoneRef.current = false;
    bargedInRef.current = true;  // Mark as interrupted — ignore late response.done
    setupAudioContext();
  }, [setupAudioContext]);

  // ---------- RESAMPLE AUDIO ----------
  const resampleAudio = useCallback(
    (audioData: Float32Array, fromRate: number, toRate: number) => {
      if (fromRate === toRate) return audioData;

      const ratio = toRate / fromRate;
      const newLength = Math.floor(audioData.length * ratio);
      const resampled = new Float32Array(newLength);

      for (let i = 0; i < newLength; i++) {
        const sourceIndex = i / ratio;
        const index = Math.floor(sourceIndex);
        const fraction = sourceIndex - index;

        if (index + 1 < audioData.length) {
          resampled[i] =
            audioData[index] * (1 - fraction) + audioData[index + 1] * fraction;
        } else {
          resampled[i] = audioData[index] || 0;
        }
      }

      return resampled;
    },
    []
  );

  // ---------- APPLY SMOOTHING FILTER ----------
  const applySmoothingFilter = useCallback((audioData: Float32Array) => {
    const smoothed = new Float32Array(audioData.length);
    const windowSize = AUDIO_CONFIG.smoothingWindowSize;

    for (let i = 0; i < audioData.length; i++) {
      let sum = 0;
      let count = 0;

      for (
        let j = Math.max(0, i - windowSize);
        j <= Math.min(audioData.length - 1, i + windowSize);
        j++
      ) {
        sum += audioData[j];
        count++;
      }

      smoothed[i] = sum / count;
    }

    return smoothed;
  }, []);

  // ---------- APPLY FADE EFFECTS ----------
  const applyFadeEffects = useCallback((audioData: Float32Array) => {
    const fadeDuration = AUDIO_CONFIG.fadeDuration;
    const sampleRate = AUDIO_CONFIG.sampleRate;
    const fadeLength = Math.floor(fadeDuration * sampleRate);

    for (let i = 0; i < Math.min(fadeLength, audioData.length); i++) {
      const fadeGain = Math.sin(((i / fadeLength) * Math.PI) / 2);
      audioData[i] *= fadeGain;
    }

    const startFadeOut = Math.max(0, audioData.length - fadeLength);
    for (let i = startFadeOut; i < audioData.length; i++) {
      const fadeGain = Math.sin(
        (((audioData.length - i) / fadeLength) * Math.PI) / 2
      );
      audioData[i] *= fadeGain;
    }
  }, []);

  // Track scheduled end time for seamless playback
  const scheduledEndTimeRef = useRef<number>(0);
  const pendingSourcesRef = useRef<AudioBufferSourceNode[]>([]);

  // ---------- PLAY NEXT IN QUEUE (Seamless scheduling) ----------
  const playNextInQueue = useCallback(async () => {
    if (!audioContextRef.current || audioQueueRef.current.length === 0) {
      return;
    }

    // Process all queued audio immediately with scheduled playback
    try {
      await audioContextRef.current.resume();

      const currentTime = audioContextRef.current.currentTime;

      // Initialize scheduled end time if not set or in the past
      if (scheduledEndTimeRef.current < currentTime) {
        scheduledEndTimeRef.current = currentTime;
      }

      // Schedule all queued audio back-to-back
      while (audioQueueRef.current.length > 0) {
        const audioBuffer = audioQueueRef.current.shift();
        if (!audioBuffer) continue;

        const source = audioContextRef.current.createBufferSource();
        source.buffer = audioBuffer;
        source.playbackRate.value = AUDIO_CONFIG.playbackRate;

        const sourceGain = audioContextRef.current.createGain();

        // Start with fade in
        const startTime = scheduledEndTimeRef.current;
        sourceGain.gain.setValueAtTime(0, startTime);
        sourceGain.gain.linearRampToValueAtTime(
          AUDIO_CONFIG.fadeInSteps.step1,
          startTime + AUDIO_CONFIG.fadeInSteps.step1Time
        );
        sourceGain.gain.linearRampToValueAtTime(
          AUDIO_CONFIG.fadeInSteps.step2,
          startTime + AUDIO_CONFIG.fadeInSteps.step2Time
        );

        source.connect(sourceGain);

        if (gainNodeRef.current) {
          sourceGain.connect(gainNodeRef.current);
        } else {
          sourceGain.connect(audioContextRef.current.destination);
        }

        // Track this source for potential cancellation
        pendingSourcesRef.current.push(source);

        // Schedule playback at the next available time
        source.start(startTime);

        // Update scheduled end time for next audio
        const duration = audioBuffer.duration / AUDIO_CONFIG.playbackRate;
        scheduledEndTimeRef.current = startTime + duration;

        // First audio starting - notify
        if (!isPlayingRef.current) {
          isPlayingRef.current = true;
          onTTSAudioStart?.();
        }

        console.log(`🎵 [AudioManager] Scheduled audio | startTime=${startTime.toFixed(3)} | duration=${duration.toFixed(3)}s`);

        // Handle end of this source
        source.onended = () => {
          // Remove from pending sources
          const idx = pendingSourcesRef.current.indexOf(source);
          if (idx > -1) {
            pendingSourcesRef.current.splice(idx, 1);
          }

          onTTSAudioComplete?.();

          // Check if all audio finished AND server confirmed no more chunks coming
          if (pendingSourcesRef.current.length === 0 && audioQueueRef.current.length === 0 && responseDoneRef.current) {
            isPlayingRef.current = false;
            currentSourceRef.current = null;
            currentPlayingTextRef.current = "";
            responseDoneRef.current = false;
            onAllAudioComplete?.();
          }
        };
      }
    } catch (error) {
      console.error("Error playing audio:", error);
      isPlayingRef.current = false;
    }
  }, [onTTSAudioStart, onTTSAudioComplete, onAllAudioComplete]);

  // Convert PCM 16-bit to Float32Array
  const convertPCM16ToFloat32 = useCallback(
    (pcm16Data: Int16Array): Float32Array => {
      const float32Data = new Float32Array(pcm16Data.length);
      for (let i = 0; i < pcm16Data.length; i++) {
        // Convert from 16-bit PCM (-32768 to 32767) to float32 (-1.0 to 1.0)
        float32Data[i] = pcm16Data[i] / 32768.0;
      }
      return float32Data;
    },
    []
  );

  // ---------- PROCESS AUDIO FROM ARRAYBUFFER ----------
  const processAudioFromArrayBuffer = useCallback(
    async (arrayBuffer: ArrayBuffer, sampleRate: number = 16000) => {
      if (!audioContextRef.current) return;

      try {
        let audioBuffer: AudioBuffer;

        // Check if data has a standard audio file header (WAV or OGG only)
        // Note: MP3 check removed — 0xFF 0xEx false-positives on raw PCM silence bytes
        const header = new Uint8Array(arrayBuffer, 0, Math.min(4, arrayBuffer.byteLength));
        const isEncodedAudio =
          (header[0] === 0x52 && header[1] === 0x49 && header[2] === 0x46 && header[3] === 0x46) || // RIFF (WAV)
          (header[0] === 0x4F && header[1] === 0x67 && header[2] === 0x67 && header[3] === 0x53);   // OggS (Opus)

        if (isEncodedAudio) {
          // Decode standard audio format (WAV, MP3, OGG, etc.)
          audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
        } else {
          // Raw PCM 16-bit mono at 16kHz (from backend TTS)
          const pcm16Data = new Int16Array(arrayBuffer);
          const float32Data = convertPCM16ToFloat32(pcm16Data);

          audioBuffer = audioContextRef.current.createBuffer(
            1, // mono
            float32Data.length,
            sampleRate // Backend sends PCM at specified sample rate
          );
          audioBuffer.getChannelData(0).set(float32Data);
        }

        // Apply audio processing if needed
        if (AUDIO_CONFIG.enableSmoothing || AUDIO_CONFIG.enableResampling) {
          const channelData = audioBuffer.getChannelData(0);
          let processedData = new Float32Array(channelData.length);
          processedData.set(channelData);

          if (AUDIO_CONFIG.enableSmoothing) {
            processedData = applySmoothingFilter(processedData);
          }

          if (
            AUDIO_CONFIG.enableResampling &&
            audioBuffer.sampleRate !== AUDIO_CONFIG.sampleRate
          ) {
            const resampledData = resampleAudio(
              processedData,
              audioBuffer.sampleRate,
              AUDIO_CONFIG.sampleRate
            );
            processedData = new Float32Array(resampledData.length);
            processedData.set(resampledData);
          }

          applyFadeEffects(processedData);

          // Create new audio buffer with processed data
          const newAudioBuffer = audioContextRef.current.createBuffer(
            1,
            processedData.length,
            AUDIO_CONFIG.sampleRate
          );
          newAudioBuffer.copyToChannel(processedData, 0);

          audioQueueRef.current.push(newAudioBuffer);
        } else {
          audioQueueRef.current.push(audioBuffer);
        }

        // New audio arriving — reset stale flags from previous turn
        // This prevents turn N's response.done from triggering playback.completed for turn N+1
        bargedInRef.current = false;
        responseDoneRef.current = false;
        playNextInQueue();
      } catch (error) {
        console.error("Error processing audio from ArrayBuffer:", error);
      }
    },
    [
      applySmoothingFilter,
      applyFadeEffects,
      resampleAudio,
      playNextInQueue,
      convertPCM16ToFloat32,
    ]
  );

  // ---------- CONNECT TTS ----------
  const connectTTS = useCallback(() => {
    setupAudioContext();
  }, [setupAudioContext]);

  // ---------- STOP AUDIO ----------
  const stopAudio = useCallback(() => {
    const wasPlaying = isPlayingRef.current;

    // Stop all pending sources
    for (const source of pendingSourcesRef.current) {
      try {
        source.stop();
      } catch (error) {
        // Source might already be stopped
      }
    }
    pendingSourcesRef.current = [];

    if (currentSourceRef.current) {
      try {
        currentSourceRef.current.stop();
      } catch (error) {
        // Source might already be stopped
      }
      currentSourceRef.current = null;
    }
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    currentPlayingTextRef.current = "";
    scheduledEndTimeRef.current = 0;
    responseDoneRef.current = false;
    bargedInRef.current = true;  // Mark as interrupted — ignore late response.done
  }, []);

  // Get playback stream for echo cancellation
  const getPlaybackStream = useCallback(() => {
    return playbackStreamRef.current;
  }, []);

  // Set current playing text (for WebSocket audio tracking)
  const setCurrentPlayingText = useCallback((text: string) => {
    currentPlayingTextRef.current = text;
  }, []);

  // Check if all audio has finished playing
  const hasAudioFinished = useCallback(() => {
    return audioQueueRef.current.length === 0 &&
           pendingSourcesRef.current.length === 0 &&
           !isPlayingRef.current &&
           !isStreamingRef.current;
  }, []);

  // Mark that server has finished sending all audio for current response
  // If queue is already empty, fire onAllAudioComplete immediately
  const markResponseDone = useCallback(() => {
    // If audio was interrupted by barge-in, ignore this late response.done
    if (bargedInRef.current) {
      console.log("📦 [RESPONSE.DONE] Ignored — audio was interrupted by barge-in");
      return;
    }
    responseDoneRef.current = true;
    if (pendingSourcesRef.current.length === 0 && audioQueueRef.current.length === 0 && !isPlayingRef.current) {
      // All audio already played before response.done arrived
      responseDoneRef.current = false;
      onAllAudioComplete?.();
    }
  }, [onAllAudioComplete]);

  // ---------- STREAMING AUDIO METHODS ----------

  // Helper function to schedule and play audio chunk immediately
  const playAudioChunk = useCallback((pcm16Data: Int16Array) => {
    if (!audioContextRef.current) return;

    try {
      // Convert PCM 16-bit to Float32
      const float32Data = new Float32Array(pcm16Data.length);
      for (let i = 0; i < pcm16Data.length; i++) {
        float32Data[i] = pcm16Data[i] / 32768.0;
      }

      // Create AudioBuffer
      const audioBuffer = audioContextRef.current.createBuffer(
        1, // mono
        float32Data.length,
        AUDIO_CONFIG.streamingSampleRate
      );
      audioBuffer.copyToChannel(float32Data, 0);

      // Create source and schedule playback
      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;

      if (streamingGainNodeRef.current) {
        source.connect(streamingGainNodeRef.current);
      } else if (gainNodeRef.current) {
        source.connect(gainNodeRef.current);
      } else {
        source.connect(audioContextRef.current.destination);
      }

      // Schedule at the next available time
      const currentTime = audioContextRef.current.currentTime;
      const startTime = Math.max(currentTime, streamingScheduledTimeRef.current);

      source.start(startTime);

      // Update scheduled time for next chunk
      const duration = float32Data.length / AUDIO_CONFIG.streamingSampleRate;
      streamingScheduledTimeRef.current = startTime + duration;

      streamingSourceRef.current = source;

      console.log(`🎵 [STREAMING] Scheduled chunk | samples=${pcm16Data.length} | startTime=${startTime.toFixed(3)} | duration=${duration.toFixed(3)}s`);
    } catch (error) {
      console.error("❌ [STREAMING] Error playing chunk:", error);
    }
  }, []);

  // Start streaming audio mode
  const startStreamingAudio = useCallback(() => {
    if (!audioContextRef.current) {
      setupAudioContext();
    }

    // Resume audio context if suspended
    if (audioContextRef.current?.state === "suspended") {
      audioContextRef.current.resume();
    }

    // Reset streaming state
    isStreamingRef.current = true;
    streamingBufferRef.current = [];
    streamingStartedRef.current = false;
    streamingChunkCountRef.current = 0;
    streamingScheduledTimeRef.current = audioContextRef.current?.currentTime || 0;

    // Create streaming gain node for smooth fade in/out
    if (audioContextRef.current) {
      streamingGainNodeRef.current = audioContextRef.current.createGain();
      streamingGainNodeRef.current.gain.value = 1.0; // Start at full volume

      if (gainNodeRef.current) {
        streamingGainNodeRef.current.connect(gainNodeRef.current);
      } else {
        streamingGainNodeRef.current.connect(audioContextRef.current.destination);
      }
    }

    console.log("🎵 [STREAMING] Started streaming audio mode");
    onStreamingAudioStart?.();
  }, [setupAudioContext, onStreamingAudioStart]);

  // Process streaming audio chunk (raw PCM 16-bit)
  const processStreamingAudioChunk = useCallback((arrayBuffer: ArrayBuffer) => {
    if (!isStreamingRef.current) {
      console.warn("🎵 [STREAMING] Received chunk but not in streaming mode");
      return;
    }

    if (!audioContextRef.current) {
      console.warn("🎵 [STREAMING] No audio context");
      return;
    }

    try {
      streamingChunkCountRef.current += 1;
      const chunkNum = streamingChunkCountRef.current;

      // Convert ArrayBuffer to Int16Array (PCM 16-bit)
      const pcm16Data = new Int16Array(arrayBuffer);

      console.log(`🎵 [STREAMING] Received chunk #${chunkNum} | bytes=${arrayBuffer.byteLength} | samples=${pcm16Data.length}`);

      // First chunk - start playback immediately
      if (!streamingStartedRef.current) {
        streamingStartedRef.current = true;
        console.log(`🎵 [STREAMING] First chunk received, starting playback`);
      }

      // Play this chunk immediately (no buffering)
      playAudioChunk(pcm16Data);

    } catch (error) {
      console.error("❌ [STREAMING] Error processing audio chunk:", error);
    }
  }, [playAudioChunk]);

  // Stop streaming audio mode
  const stopStreamingAudio = useCallback(() => {
    if (!isStreamingRef.current) {
      return;
    }

    const totalChunks = streamingChunkCountRef.current;
    console.log(`🎵 [STREAMING] Stopping streaming audio mode | total chunks=${totalChunks}`);

    // Fade out
    if (streamingGainNodeRef.current && audioContextRef.current) {
      const now = audioContextRef.current.currentTime;
      streamingGainNodeRef.current.gain.setValueAtTime(
        streamingGainNodeRef.current.gain.value,
        now
      );
      streamingGainNodeRef.current.gain.linearRampToValueAtTime(0, now + 0.1);

      // Disconnect after fade out
      setTimeout(() => {
        if (streamingGainNodeRef.current) {
          try {
            streamingGainNodeRef.current.disconnect();
          } catch (e) {
            // Ignore
          }
          streamingGainNodeRef.current = null;
        }
      }, 150);
    }

    // Reset streaming state
    isStreamingRef.current = false;
    streamingBufferRef.current = [];
    streamingStartedRef.current = false;
    streamingSourceRef.current = null;
    streamingChunkCountRef.current = 0;

    onStreamingAudioEnd?.();
  }, [onStreamingAudioEnd]);

  // Check if currently streaming
  const isStreaming = useCallback(() => {
    return isStreamingRef.current;
  }, []);

  const getDiagnostics = useCallback(() => {
    const ctx = audioContextRef.current;
    const gain = gainNodeRef.current;
    return {
      audio_context_state: ctx ? ctx.state : "null",
      audio_context_sample_rate: ctx ? ctx.sampleRate : null,
      audio_context_current_time_ms: ctx ? Math.round(ctx.currentTime * 1000) : null,
      gain_value: gain ? gain.gain.value : null,
      muted: gain ? gain.gain.value === 0 : null,
    };
  }, []);

  return {
    setupAudioContext,
    resetAudioCompletely,
    connectTTS,
    stopAudio,
    processAudioFromArrayBuffer,
    playNextInQueue,
    getPlaybackStream,
    setCurrentPlayingText,
    hasAudioFinished,
    markResponseDone,
    getDiagnostics,
    // Streaming audio methods
    startStreamingAudio,
    processStreamingAudioChunk,
    stopStreamingAudio,
    isStreaming,
  };
};
